import * as React from 'react';
import { useMutation, useQueryClient } from 'react-query';
import { HttpClient } from 'apiclient/ApiClient';
import { formatDateToYYYYMMDD } from 'utils/reportUtils';
import { waitForReportData } from 'utils/reportUtils';

export interface UseReportCreationConfig {
    apiEndpoint: string;
    queryKeyToInvalidate: string;
    buildRequestUrl: (params: ReportCreationParams) => string;
    onSuccess?: (reportId: string) => void;
}

export interface ReportCreationParams {
    startDate: string;
    endDate: string;
    [key: string]: string; // Additional params like classId, organizationId
}

export interface ReportCreationResponse {
    report_id: string;
    message: string;
    error?: string;
}

export interface UseReportCreationReturn {
    // State
    isLoading: boolean;
    error: string;
    
    // Actions
    createReport: (params: ReportCreationParams) => void;
    clearError: () => void;
    validateDateRange: (startDate: string | null, endDate: string | null) => string | null;
}

export function useReportCreation(config: UseReportCreationConfig): UseReportCreationReturn {
    const [error, setError] = React.useState('');
    const httpClient = HttpClient();
    const queryClient = useQueryClient();
    
    const {
        queryKeyToInvalidate,
        buildRequestUrl,
        onSuccess
    } = config;

    const createReportMutation = useMutation<ReportCreationResponse, Error, ReportCreationParams>(
        async (params: ReportCreationParams) => {
            
            // Build the request URL with query parameters
            const requestUrl = buildRequestUrl(params);
            
            const response = await httpClient.post(requestUrl, {});
            
            return response as unknown as ReportCreationResponse;
        },
        {
            onSuccess: async (data) => {
                
                // Check for explicit error field first
                if (data && data.error) {
                    setError(data.error);
                    return;
                }
                
                // Check for "No data found" message
                if (data && data.message && data.message.includes('No data found')) {
                    setError('No data found for the specified date range. Please try a different date range.');
                    return;
                }
                
                // Check for successful report creation
                if (data && data.report_id && data.report_id !== '') {
                    
                    // Invalidate the reports list to trigger refetch
                    queryClient.invalidateQueries(queryKeyToInvalidate);
                    
                    // Wait for the report data to be available before proceeding
                    await waitForReportData(data.report_id, httpClient, config.apiEndpoint);
                    
                    // Call the success callback
                    if (onSuccess) {
                        onSuccess(data.report_id);
                    }
                    return;
                }
                
                // Fallback for unexpected response format
                setError('Unexpected response format');
            },
            onError: (error: Error) => {
                setError(error.message || 'Failed to create report');
            }
        }
    );

    const validateDateRange = React.useCallback((
        startDate: string | null, 
        endDate: string | null
    ): string | null => {
        if (!startDate || !endDate) {
            return 'Please select both start and end dates';
        }
        
        const start = new Date(startDate);
        const end = new Date(endDate);
        
        if (start > end) {
            return 'Start date must be before end date';
        }
        
        return null;
    }, []);

    const createReport = React.useCallback((params: ReportCreationParams) => {
        const validationError = validateDateRange(params.startDate, params.endDate);
        if (validationError) {
            setError(validationError);
            return;
        }

        setError('');
        
        // Convert dates to YYYY-MM-DD format for API
        const formattedParams = {
            ...params,
            startDate: formatDateToYYYYMMDD(params.startDate),
            endDate: formatDateToYYYYMMDD(params.endDate)
        };
        
        createReportMutation.mutate(formattedParams);
    }, [createReportMutation, validateDateRange]);

    const clearError = React.useCallback(() => {
        setError('');
    }, []);

    return {
        // State
        isLoading: createReportMutation.isLoading,
        error,
        
        // Actions
        createReport,
        clearError,
        validateDateRange
    };
}